import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
    FiBriefcase, FiCalendar, FiTruck, FiUsers, FiBarChart2,
    FiArrowRight, FiClock, FiCheckCircle, FiLogOut, FiPackage,
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';

// ─── Trial countdown ──────────────────────────────────────────────────────────
const TrialBadge = ({ trialEndDate }) => {
    const { days, hours, expired } = useMemo(() => {
        if (!trialEndDate) return { days: 0, hours: 0, expired: true };
        const diff = new Date(trialEndDate) - new Date();
        if (diff <= 0) return { days: 0, hours: 0, expired: true };
        const totalHours = Math.floor(diff / (1000 * 60 * 60));
        return { days: Math.floor(totalHours / 24), hours: totalHours % 24, expired: false };
    }, [trialEndDate]);

    if (expired) {
        return (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-full">
                <div className="w-2 h-2 rounded-full bg-red-400" />
                <span className="text-sm font-medium text-red-600">Trial expired</span>
            </div>
        );
    }

    return (
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full">
            <FiClock className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-sm font-medium text-amber-700">
                Sandbox trial · {days > 0 ? `${days}d ` : ''}{hours}h remaining
            </span>
        </div>
    );
};

// ─── Feature card ─────────────────────────────────────────────────────────────
const FeatureCard = ({ icon: Icon, color, title, desc, href }) => (
    <Link to={href} className="group flex flex-col gap-3 p-5 bg-white rounded-2xl border border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color}`}>
            <Icon className="w-5 h-5" />
        </div>
        <div>
            <h3 className="text-sm font-semibold text-gray-800 group-hover:text-indigo-700 transition-colors">{title}</h3>
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{desc}</p>
        </div>
        <div className="flex items-center gap-1 text-xs font-medium text-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity mt-auto">
            Try it <FiArrowRight className="w-3 h-3" />
        </div>
    </Link>
);

// ─── Getting-started step ─────────────────────────────────────────────────────
const Step = ({ num, title, desc, done = false }) => (
    <div className="flex items-start gap-4">
        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${done ? 'bg-green-100 text-green-600' : 'bg-indigo-100 text-indigo-600'}`}>
            {done ? <FiCheckCircle className="w-4 h-4" /> : num}
        </div>
        <div className="pt-0.5">
            <p className={`text-sm font-semibold ${done ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{title}</p>
            <p className="text-xs text-gray-400 mt-0.5">{desc}</p>
        </div>
    </div>
);

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function WelcomeDashboard() {
    const { userProfile, company, signOut } = useAuth();
    const slug = company?.slug ?? '';

    const features = [
        {
            icon: FiBriefcase,
            color: 'bg-blue-50 text-blue-600',
            title: 'Job Management',
            desc: 'Create and track jobs from quote to delivery.',
            href: `/${slug}/assignment/list`,
        },
        {
            icon: FiCalendar,
            color: 'bg-purple-50 text-purple-600',
            title: 'Schedule',
            desc: 'Plan editor, production and delivery schedules.',
            href: `/${slug}/schedule`,
        },
        {
            icon: FiPackage,
            color: 'bg-indigo-50 text-indigo-600',
            title: 'Production',
            desc: 'Manage the production workflow and phases.',
            href: `/${slug}/schedule/production`,
        },
        {
            icon: FiTruck,
            color: 'bg-green-50 text-green-600',
            title: 'Delivery',
            desc: 'Schedule and track job deliveries.',
            href: `/${slug}/schedule/delivery`,
        },
        {
            icon: FiUsers,
            color: 'bg-orange-50 text-orange-600',
            title: 'Customers',
            desc: 'Maintain your customer and project records.',
            href: `/${slug}/customers`,
        },
        {
            icon: FiBarChart2,
            color: 'bg-pink-50 text-pink-600',
            title: 'Analytics',
            desc: 'View reports on jobs, output and team activity.',
            href: `/${slug}/analytics`,
        },
    ];

    const firstName = userProfile?.full_name?.split(' ')[0] || userProfile?.username || 'there';

    return (
        <div className="mobile-dashboard-page min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 p-4 sm:p-8">
            <div className="max-w-5xl mx-auto space-y-8">

                {/* ── Top bar ── */}
                <div className="flex items-center justify-between">
                    <TrialBadge trialEndDate={company?.trial_end_date} />
                    <button
                        onClick={signOut}
                        className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
                    >
                        <FiLogOut className="w-3.5 h-3.5" />
                        Sign out
                    </button>
                </div>

                {/* ── Hero ── */}
                <div className="bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-700 rounded-3xl p-8 sm:p-12 text-white relative overflow-hidden">
                    {/* decorative blobs */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/3 translate-x-1/3" />
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/3 -translate-x-1/3" />

                    <div className="relative">
                        <p className="text-indigo-200 text-sm font-medium mb-2">Welcome to Notify</p>
                        <h1 className="text-3xl sm:text-4xl font-bold mb-3">
                            Hey, {firstName}! 👋
                        </h1>
                        <p className="text-indigo-100 text-base sm:text-lg max-w-xl leading-relaxed">
                            Your sandbox workspace is ready. Explore all the features below —
                            everything you create is private to your account.
                        </p>

                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link
                                to={`/${slug}/assignment/new`}
                                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-indigo-700 font-semibold rounded-xl text-sm hover:bg-indigo-50 transition-colors shadow-md"
                            >
                                <FiBriefcase className="w-4 h-4" />
                                Create your first job
                            </Link>
                            <Link
                                to={`/${slug}/schedule`}
                                className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-500/50 text-white font-semibold rounded-xl text-sm hover:bg-indigo-500/70 transition-colors border border-white/20"
                            >
                                <FiCalendar className="w-4 h-4" />
                                View schedules
                            </Link>
                        </div>
                    </div>
                </div>

                {/* ── Features grid ── */}
                <div>
                    <h2 className="text-lg font-semibold text-gray-800 mb-4">Explore features</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                        {features.map(f => <FeatureCard key={f.title} {...f} />)}
                    </div>
                </div>

                {/* ── Getting started + Sandbox info ── */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">

                    {/* Getting started steps */}
                    <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5">
                        <h2 className="text-sm font-semibold text-gray-800">Getting started</h2>
                        <div className="space-y-4">
                            <Step num={1} title="Account created" desc="Your sandbox account is ready to use." done />
                            <Step num={2} title="Create a job" desc="Add your first job to see the full workflow." />
                            <Step num={3} title="Set up a schedule" desc="Plan production or delivery timelines." />
                            <Step num={4} title="Invite a teammate" desc="Add a team member to collaborate." />
                        </div>
                    </div>

                    {/* Sandbox info */}
                    <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
                        <h2 className="text-sm font-semibold text-gray-800">About your sandbox</h2>
                        <ul className="space-y-3">
                            {[
                                'Your data is completely private — no one else sees it.',
                                'Everything you create will be automatically cleaned up after the trial period ends.',
                                'All features are fully functional — no restrictions.',
                                'Ready to use it for real? Contact us to upgrade to a full plan.',
                            ].map((item, i) => (
                                <li key={i} className="flex items-start gap-2.5 text-sm text-gray-600">
                                    <FiCheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                    {item}
                                </li>
                            ))}
                        </ul>

                        <a
                            href="mailto:hello@notifyapp.com.au"
                            className="inline-flex items-center gap-1.5 mt-2 px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-opacity"
                        >
                            Contact us to upgrade
                            <FiArrowRight className="w-3.5 h-3.5" />
                        </a>
                    </div>
                </div>

            </div>
        </div>
    );
}
